<script setup lang="ts">
/**
 * What a list says when it has nothing in it (SPEC §9.1, MT-113).
 *
 * SPEC §9.1 asks that empty states explain the next action, which is a higher
 * bar than saying the list is empty — the person can already see that. The
 * screens each grew their own sentence in their own class (`events__empty`,
 * `templates__empty`, `household__empty`) and most of them stopped at the
 * observation. This is one shape for all of them, and the `action` slot is
 * what makes the difference: an empty state without a way out is a dead end
 * dressed as an explanation.
 *
 * Two densities, because these appear in two places. Full size is a screen
 * whose whole content is missing — no households, no meals — and deserves the
 * room. `compact` is a section inside a populated screen, where the same
 * treatment would shout louder than the content around it.
 *
 * The icon is decorative and marked so. It repeats what the headline already
 * says, and a screen reader announcing "tray arrow icon, no meals yet" is
 * worse than the headline alone.
 */
withDefaults(
  defineProps<{
    /** The plain-language statement of what is missing. */
    headline: string
    /** Why it is empty, or what putting something here would do. */
    description?: string
    /** A short label above the headline, e.g. "First step". */
    overline?: string
    /** An mdi name. Decorative only. */
    icon?: string
    /** Tighter treatment for a section inside an otherwise full screen. */
    compact?: boolean
    /**
     * Overrides the default test id.
     *
     * A screen can hold several of these — an event dashboard has one for
     * unclaimed dishes and another for reminders — and `empty-state` cannot
     * identify either. Callers that already had a name keep it.
     */
    testid?: string
  }>(),
  {
    description: undefined,
    overline: undefined,
    icon: undefined,
    compact: false,
    testid: 'empty-state',
  },
)
</script>

<template>
  <div class="empty" :class="{ 'empty--compact': compact }" :data-testid="testid" role="status">
    <v-icon
      v-if="icon"
      :icon="icon"
      :size="compact ? 28 : 40"
      class="empty__icon"
      aria-hidden="true"
    />

    <p v-if="overline" class="text-overline empty__overline">{{ overline }}</p>

    <p class="empty__headline" :class="compact ? 'text-subtitle-1' : 'text-h6'">
      {{ headline }}
    </p>

    <p v-if="description" class="text-body-2 empty__description">{{ description }}</p>

    <!--
      Not wrapped in a v-if. A slot that is passed nothing renders nothing, and
      testing for its presence here would mean every caller without an action
      still paid for the margin below.
    -->
    <div class="empty__action">
      <slot name="action" />
    </div>
  </div>
</template>

<style scoped>
.empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2.5rem 1rem;
}

.empty--compact {
  padding: 1.25rem 0.5rem;
}

.empty__icon {
  opacity: 0.4;
  margin-bottom: 0.75rem;
}

.empty__overline {
  margin: 0 0 0.25rem;
  opacity: 0.6;
}

.empty__headline {
  margin: 0;
  font-weight: 600;
}

.empty__description {
  /*
    A measure rather than a width. Centred text past about 50 characters a line
    is genuinely harder to read, and these appear inside containers of every
    width.
  */
  max-width: 34rem;
  margin: 0.5rem 0 0;
  opacity: 0.75;
}

.empty__action:not(:empty) {
  margin-top: 1.25rem;
}
</style>
